/**
 * The `acceptance` object every signup request carries (RW-422, core-services #899).
 *
 * It records which boxes the person ticked on the screen that created the account, against
 * the version of the texts that screen showed. The backend refuses a version it does not
 * know (409 `signup_texts_outdated`) and a missing required box (422
 * `signup_terms_not_accepted`); an absent object is still accepted while the backend runs in
 * transition mode, so this can ship before or after it.
 *
 * Bump the texts version together with the backend whenever the wording of a box changes.
 */
export const WORKER_SIGNUP_TEXTS_VERSION = 'w-2026-11-1';
export const EMPLOYER_SIGNUP_TEXTS_VERSION = 'e-2026-11-1';
/**
 * Texts version of the stand-alone news-and-tips box a worker answers inside the app, for the
 * accounts created without a screen (Hirematic/Indeed): same wording as the optional signup box,
 * sent to `POST /accounts/marketing_consent`.
 */
export const WORKER_MARKETING_CONSENT_TEXTS_VERSION = 'wc-2026-11-1';

export interface WorkerSignupAcceptance {
  texts_version: string;
  terms: true;
  /** The optional box: news and tips from Restworld, plus email open tracking. */
  marketing_and_tracking: boolean;
  idempotency_key: string;
}

export interface EmployerSignupAcceptance {
  texts_version: string;
  terms: true;
  /** Specific approval of the clauses (artt. 1341-1342 c.c.). */
  clauses: true;
  /** The optional box: news and tips from Restworld, plus email open tracking. */
  tracking: boolean;
  idempotency_key: string;
}

/** The `acceptance` object of `POST /accounts/marketing_consent`: the optional box alone. */
export interface WorkerMarketingConsentAcceptance {
  texts_version: string;
  marketing_and_tracking: boolean;
  idempotency_key: string;
}

export type SignupAcceptanceError = 'signup_texts_outdated' | 'signup_terms_not_accepted';

/** One key per form attempt: a retry of the same submit sends the same key. */
export function generateSignupIdempotencyKey(): string {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export function buildWorkerSignupAcceptance(
  marketingAndTracking: boolean | undefined,
  idempotencyKey: string
): WorkerSignupAcceptance {
  return {
    texts_version: WORKER_SIGNUP_TEXTS_VERSION,
    terms: true,
    marketing_and_tracking: !!marketingAndTracking,
    idempotency_key: idempotencyKey,
  };
}

export function buildWorkerMarketingConsentAcceptance(
  marketingAndTracking: boolean,
  idempotencyKey: string
): WorkerMarketingConsentAcceptance {
  return {
    texts_version: WORKER_MARKETING_CONSENT_TEXTS_VERSION,
    marketing_and_tracking: marketingAndTracking,
    idempotency_key: idempotencyKey,
  };
}

export function buildEmployerSignupAcceptance(
  tracking: boolean | undefined,
  idempotencyKey: string
): EmployerSignupAcceptance {
  return {
    texts_version: EMPLOYER_SIGNUP_TEXTS_VERSION,
    terms: true,
    clauses: true,
    tracking: !!tracking,
    idempotency_key: idempotencyKey,
  };
}

/**
 * The acceptance refusal a failed request carries, or null. Reads both the shape the
 * data-services interceptor rejects with (`{ code, data }`) and the raw axios one.
 */
export function signupAcceptanceErrorOf(error: unknown): SignupAcceptanceError | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const err = error as any;
  const body = err?.data ?? err?.response?.data;
  const code = typeof body?.error === 'string' ? body.error : undefined;
  return code === 'signup_texts_outdated' || code === 'signup_terms_not_accepted' ? code : null;
}
